import { FC, useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { useNavigate, useLocation } from "react-router";
import { Alert, Box, Button, Typography } from "@mui/material";

import ChatInputPredefaultQuestions from "./chat-input/ChatInputPredefaultQuestions";
import { ChatHelper } from "../../helpers/chatHelper";
import AttachedFileItem from "../../ui/AttachedFileItem";
import { DEFAULT_FILE_ACCEPT } from "../../../config/channels";
import {
  attachmentRejectionMessage,
  isFileAccepted,
} from "../../ui/DropZoneWrapper";
import {
  CHAT_INPUT_MAX_CHARS,
  CHAT_PASTE_AS_FILE_THRESHOLD,
} from "../../../config/constants";
import ChatInputActions from "./chat-input/ChatInputActions";
import MockLlmToggle from "./chat-input/MockLlmToggle";
import useNotifier from "../../../hooks/useNotifier";
import { useChatStore } from "../../../hooks/useChatStore";
import { useClaimsStore } from "../../../hooks/useClaimsStore";
import { websocketService } from "../../../services/websocketService";
import { PreDefaultQuestionType } from "../../../types/claims";
import { APP_EVENTS } from "../../../types/wsEvents";
import { SIDEBAR_TAB_TYPES } from "../../../types/sidebar";
import { useCapabilities } from "../../../contexts/permissions/useCapabilities";

// FileList can't be constructed directly; DataTransfer is the standard way to
// build one, but it isn't implemented in every test/runtime environment.
const toFileList = (files: File[]): FileList => {
  if (typeof DataTransfer !== "undefined") {
    const dataTransfer = new DataTransfer();
    files.forEach((file) => dataTransfer.items.add(file));
    return dataTransfer.files;
  }
  const fallback = files as unknown as FileList & {
    item: (index: number) => File | null;
  };
  fallback.item = (index: number) => files[index] ?? null;
  return fallback;
};

interface ChatInputProps {
  placeholder?: string;
  preDefaultQuestions?: PreDefaultQuestionType[];
  forceEnable?: boolean; // When claimActivePredefaultQuestion is set, enable input by default (in case we are in chat page)
  requiredInputs?: string; // Review pre-chats: a document must be attached before the first message can be sent
}

const ChatInput: FC<ChatInputProps> = observer(
  ({
    placeholder = "Ask about contract terms, coverage, or reimbursement…",
    preDefaultQuestions,
    forceEnable,
    requiredInputs,
  }) => {
    const navigate = useNavigate();
    const notify = useNotifier();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const isSubmittingRef = useRef(false);
    const chatStore = useChatStore();
    const claimsStore = useClaimsStore();
    const { canCloneChat } = useCapabilities();
    const location = useLocation();

    const [messageInput, setMessageInput] = useState("");
    const [isCloning, setIsCloning] = useState(false);
    const MIN_TEXTAREA_ROWS = 1;
    const MAX_TEXTAREA_ROWS = 10;

    // Clear chat input text on url change
    useEffect(() => {
      setMessageInput("");
    }, [chatStore.activeChatId]);

    // Clone the current (closed/view-only) chat into a new writable chat and
    // open it. Lets the user continue from a chat they can no longer write to.
    const handleCloneCurrentChat = async () => {
      const sourceId = chatStore.activeChatId;
      if (!sourceId || isCloning) return;
      setIsCloning(true);
      try {
        // cloneChat emits CHANNEL_ADD so the clone shows up in the sidebar
        // (refreshChannels would be a no-op for the unchanged filter).
        const newChat = await websocketService.cloneChat(sourceId);
        if (newChat?.id) {
          navigate(`/channels/${newChat.id}`);
          // Dispatch custom event to reset selected sidebar tab to highlight the cloned chat
          window.dispatchEvent(
            new CustomEvent(APP_EVENTS.SET_SIDEBAR_TAB, {
              detail: SIDEBAR_TAB_TYPES.all,
            }),
          );
          chatStore.setIsChatLoading(true);
        } else {
          notify.showError("Failed to clone chat.");
        }
      } catch (error) {
        notify.showError(
          error instanceof Error
            ? error.message
            : "An error occurred while cloning the chat.",
        );
      } finally {
        setIsCloning(false);
      }
    };

    // Expand textarea while typing (wrapped lines) and on newlines, 1–5 rows then scroll
    useEffect(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      ta.style.height = "auto";
      const lineHeight = parseInt(getComputedStyle(ta).lineHeight, 10) || 24;
      const minH = MIN_TEXTAREA_ROWS * lineHeight;
      const maxH = MAX_TEXTAREA_ROWS * lineHeight;
      const h = Math.min(maxH, Math.max(minH, ta.scrollHeight));
      ta.style.height = `${h}px`;
      ta.style.overflowY = ta.scrollHeight > maxH ? "auto" : "hidden";
    }, [messageInput]);

    // A refused turn hands its text back (REV-2339) so the user can resend
    // rather than retype what the composer already cleared.
    useEffect(() => {
      if (typeof chatStore.rejectedInput === "string") {
        // A rejection can land seconds after submit, so only refill a composer
        // the user has not already started typing into again.
        setMessageInput((current) =>
          current.trim() ? current : chatStore.rejectedInput ?? current,
        );
        chatStore.clearRejectedInput();
      }
    }, [chatStore, chatStore.rejectedInput]);

    useEffect(() => {
      if (chatStore.notice) {
        notify.showWarning(chatStore.notice);
        chatStore.clearNotice();
      }
    }, [chatStore, chatStore.notice, notify]);

    // Claim-ingested EDI files don't attach to the message, so they don't
    // count toward enabling send
    const hasNoUploadedFiles = chatStore.attachableFiles.length === 0;
    // ...but they still satisfy a review's required documents
    const isMissingRequiredInputs =
      !!requiredInputs &&
      !chatStore.uploadedFiles.some((file) => file.state === "uploaded");
    const isOverCharLimit = messageInput.length > CHAT_INPUT_MAX_CHARS;
    const activePredefaultQuestion = claimsStore.claimActivePredefaultQuestion;
    // Hard blocks always apply, even when a predefault question is pending or
    // being clicked — an in-flight upload or an over-limit message must never
    // be silently dropped just because "content" is being supplied some other way.
    const hardBlocked =
      (chatStore.isChatLoading && location.pathname?.includes("/channels")) ||
      chatStore.isFileUploadInProgress ||
      isOverCharLimit ||
      isMissingRequiredInputs;
    // forceEnable only exempts the transient "aborting" flag; a genuinely
    // in-flight generation (isQuestioning) still blocks Send so Enter/click
    // can't fire a second, duplicate chat while the first is still streaming.
    const streamingBlocked = forceEnable
      ? chatStore.isQuestioning
      : chatStore.isAborting || chatStore.isQuestioning;
    // A pending predefault question counts as "content" for the empty-input
    // check — it's what Send will actually submit.
    const isSubmitDisabled =
      hardBlocked ||
      streamingBlocked ||
      (!messageInput?.trim() &&
        hasNoUploadedFiles &&
        !activePredefaultQuestion);
    const predefaultSubmitState = activePredefaultQuestion
      ? hardBlocked || (!messageInput?.trim() && hasNoUploadedFiles)
      : false;

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Enter without Shift submits the form
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e as unknown as React.FormEvent<HTMLFormElement>);
      }
    };

    // Large pastes are attached as a .txt file instead of being dumped into
    // the textarea (matches ChatGPT/Claude behavior), keeping payloads and
    // token usage manageable. Smaller pastes that would still blow the
    // character budget are truncated by the textarea's maxLength, with a
    // warning so the user knows content was cut off.
    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const pastedText = e.clipboardData.getData("text");
      if (!pastedText) return;

      if (pastedText.length > CHAT_PASTE_AS_FILE_THRESHOLD) {
        e.preventDefault();
        const fileName = `Pasted Content ${new Date()
          .toISOString()
          .replace(/[:.]/g, "-")}.txt`;
        const file = new File([pastedText], fileName, { type: "text/plain" });
        chatStore.setDroppedFiles(toFileList([file]));
        notify.showInfo(
          "Pasted content was too long to insert directly, so it was added as a text file attachment instead.",
        );
        return;
      }

      const textarea = e.currentTarget;
      const selectionLength =
        (textarea.selectionEnd ?? 0) - (textarea.selectionStart ?? 0);
      const nextLength =
        messageInput.length - selectionLength + pastedText.length;

      if (nextLength > CHAT_INPUT_MAX_CHARS) {
        // Don't preventDefault: let the native maxLength truncate the value
        // so the textarea and this warning stay in sync.
        notify.showWarning(
          `Pasted text exceeds the ${CHAT_INPUT_MAX_CHARS.toLocaleString()}-character limit and has been truncated.`,
        );
      }
    };

    const onOpenAddAttachment = useCallback(() => {
      fileInputRef.current?.click();
    }, []);

    const removeFile = (fileName: string) => {
      chatStore.setUploadedFiles(
        chatStore.uploadedFiles.filter((file) => file.name !== fileName),
      );
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = e.target.files;
      if (!selectedFiles || selectedFiles.length === 0) {
        notify.showWarning("Please select at least one file");
        return;
      }

      // `accept` only filters the dialog. "All files" still returns a zip,
      // and that upload is what fails later in extraction.
      const acceptList = `${DEFAULT_FILE_ACCEPT}`
        .split(",")
        .map((token) => token.trim())
        .filter(Boolean);
      const files = Array.from(selectedFiles);
      const accepted = files.filter((file) => isFileAccepted(file, acceptList));
      const rejected = files.filter((file) => !isFileAccepted(file, acceptList));

      e.target.value = ""; // Reset so selecting the same file again fires change

      if (rejected.length > 0) {
        notify.showWarning(
          attachmentRejectionMessage(rejected.map((file) => file.name)),
        );
      }
      if (accepted.length === 0) return;

      // Not selectedFiles: it's the input's live FileList, already emptied by
      // the value reset above.
      chatStore.setDroppedFiles(toFileList(accepted));
    };

    const handleSubmit = async (
      e: React.FormEvent<HTMLFormElement>,
      question?: PreDefaultQuestionType,
    ) => {
      e.preventDefault();

      // An explicitly clicked chip (question) carries the user's immediate
      // intent and always wins over a predefault question armed elsewhere
      // (e.g. from the claim panel) — see effectiveQuestion below. Hard/streaming
      // blocks are never bypassed by either; only the "no content yet" check is.
      const effectiveQuestion = question || activePredefaultQuestion;
      const emptyContentBlocked =
        !messageInput?.trim() && hasNoUploadedFiles && !effectiveQuestion;

      // activePredefaultQuestion intentionally skips streamingBlocked/emptyContentBlocked
      // (REV-2197: a pending predefault question can submit while isQuestioning/isAborting
      // are set), but isSubmittingRef.current must still gate both branches — otherwise two
      // fast clicks on a quick-action chip (only disabled by hardBlocked) both pass the guard
      // and fire askQuestion twice, creating two chats.
      const skipCondition = activePredefaultQuestion
        ? isSubmittingRef.current || (predefaultSubmitState && !question)
        : isSubmittingRef.current ||
          hardBlocked ||
          streamingBlocked ||
          emptyContentBlocked;

      if (skipCondition) {
        return;
      }

      isSubmittingRef.current = true;

      try {
        let redirectId: string | undefined | null;
        if (activePredefaultQuestion) {
          // Armed from the claim panel: always starts a brand-new claim chat.
          // Sync claim context from the snapshot taken when the question was
          // armed, since claimsStore.claimToPreview/chatStore.claimIdToPreview
          // may have been cleared if the claim panel was closed in the meantime.
          if (activePredefaultQuestion.claimNumber) {
            chatStore.setClaimIdToPreview(activePredefaultQuestion.claimNumber);
          }
          // The blank "Ask Reve" welcome flow is meant to be a plain chat, even
          // if the user picks one of the quick-action chips from it — so it
          // never forwards a placeholder as assistant context to the backend.
          const effectivePlaceholder = activePredefaultQuestion.isWelcome
            ? undefined
            : effectiveQuestion?.placeholder;
          redirectId = await chatStore.askQuestion(
            messageInput?.trim() || effectiveQuestion?.question || "",
            undefined,
            effectiveQuestion?.agent_name,
            true,
            effectivePlaceholder,
          );

          // askQuestion only seeds the new chat's messages with the user's
          // message; prepend the predefault placeholder (already shown by
          // ClaimPredefaultQuestionPreview pre-redirect) so it doesn't flash
          // away once we land on /channels/:id. Skip when there's no
          // placeholder (e.g. the blank "Ask Reve" welcome flow) so an empty
          // system bubble isn't inserted.
          if (redirectId && effectivePlaceholder) {
            const predefaultMessage = ChatHelper.normalizeMessage(redirectId, {
              id: `predefault-${redirectId}`,
              content: effectivePlaceholder,
              sender: "other",
            });
            chatStore.setMessages([predefaultMessage, ...chatStore.messages]);
            chatStore.setIsQuestioning(true);
          }
        } else {
          // An explicit chip click (or freeform typed text): asks in the
          // current chat, no forced new chat.
          redirectId = await chatStore.askQuestion(
            effectiveQuestion?.question || messageInput,
            undefined,
            effectiveQuestion?.agent_name,
          );
        }

        setMessageInput("");
        chatStore.setUploadedFiles([]);
        if (window.location.pathname?.includes("/channels")) {
          claimsStore.setClaimActivePredefaultQuestion(null);
        }

        if (redirectId) {
          navigate(`/channels/${redirectId}`, { replace: true });
          chatStore.setIsQuestioning(true);
        }
      } finally {
        isSubmittingRef.current = false;
      }
    };

    // Restrict for chat page only
    if (chatStore.isChatLoading && location.pathname?.includes("/channels")) {
      return null;
    }

    if (
      chatStore.isInputDisabled &&
      // Skip on new chat (Ask reve from chat content)
      !(!chatStore.activeChatId && chatStore.claimIdToPreview)
      && !claimsStore.claimActivePredefaultQuestion
    ) {
      return (
        <Box
          sx={{
            p: 2,
            bgcolor: "#f5f5f5",
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="#666"
            style={{ flexShrink: 0 }}
          >
            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z" />
          </svg>
          <Typography variant="body2" sx={{ color: "#666" }}>
            {chatStore.isClosed && !chatStore.isReadOnly
              ? "This chat is closed and cannot receive new messages."
              : "View-only mode: You may view this conversation but cannot send messages."}
          </Typography>

          {/* Reading a chat back is exactly when it matters whether the replies
              were fabricated, and this footer replaces the input form entirely
              (REV-2161). */}
          <MockLlmToggle />

          {canCloneChat ? (
            <Button
              variant="outlined"
              size="small"
              onClick={handleCloneCurrentChat}
              disabled={isCloning || !chatStore.activeChatId}
              sx={{ ml: "auto", flexShrink: 0, textTransform: "none" }}
            >
              {isCloning ? "Cloning…" : "Clone to continue"}
            </Button>
          ) : null}
        </Box>
      );
    }

    return (
      <form
        onSubmit={handleSubmit}
        className={
          "w-full min-w-0 max-w-layout-container mx-auto shrink-0 mb-4 px-4"
        }
      >
        <div className="flex flex-col gap-3 p-4 pr-2 pb-2 bg-hex-white border border-hex-gray-300 rounded-2xl shadow-xs">
          {/* Uploaded files display */}
          {chatStore.chatFilesDisplay.length > 0 && (
            <div className="flex flex-row gap-1 flex-wrap max-h-[100px] overflow-y-auto">
              {chatStore.chatFilesDisplay.map((file) => (
                <AttachedFileItem
                  key={file.id}
                  file={file}
                  onRemove={removeFile}
                />
              ))}
            </div>
          )}

          {isMissingRequiredInputs && (
            <Alert
              severity="warning"
              sx={{
                width: "100%",
                // The theme caps standard alerts at 520px
                maxWidth: "none",
                boxSizing: "border-box",
                py: "4px",
                borderRadius: "10px",
                alignItems: "center",
                "& .MuiAlert-icon": { fontSize: 16, mr: 1 },
                "& .MuiAlert-icon svg": { width: 16, height: 16 },
                "& .MuiAlert-message": {
                  flex: 1,
                  fontSize: "12px",
                  fontStyle: "italic",
                  fontWeight: 500,
                },
              }}
            >
              Upload documents to start the review: {requiredInputs}
            </Alert>
          )}

          {/* Pre-default questions */}
          {preDefaultQuestions && preDefaultQuestions.length > 0 && (
            <ChatInputPredefaultQuestions
              preDefaultQuestions={preDefaultQuestions}
              onQuestionClick={handleSubmit}
              predefaultSubmitState={hardBlocked}
            />
          )}

          <textarea
            ref={textareaRef}
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={placeholder}
            rows={MIN_TEXTAREA_ROWS}
            maxLength={CHAT_INPUT_MAX_CHARS}
            className="font-manrope font-medium text-md text-hex-gray-500 bg-transparent border-none outline-hidden placeholder:text-hex-gray-500 w-full resize-none overflow-hidden"
            style={{ minHeight: "unset" }}
          />
          {messageInput.length > CHAT_INPUT_MAX_CHARS * 0.8 && (
            <Typography
              variant="caption"
              sx={{
                alignSelf: "flex-end",
                color: isOverCharLimit ? "error.main" : "text.secondary",
              }}
            >
              {isOverCharLimit
                ? `Message is too long by ${(
                    messageInput.length - CHAT_INPUT_MAX_CHARS
                  ).toLocaleString()} characters`
                : `${messageInput.length.toLocaleString()} / ${CHAT_INPUT_MAX_CHARS.toLocaleString()}`}
            </Typography>
          )}
          <input
            type="file"
            multiple={true}
            accept={DEFAULT_FILE_ACCEPT}
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
          />

          {/* Actions */}
          <ChatInputActions
            uploadedFiles={chatStore.attachableFiles}
            onAddAttachment={onOpenAddAttachment}
            isSubmitDisabled={isSubmitDisabled}
            predefaultSubmitState={predefaultSubmitState}
          />
        </div>
      </form>
    );
  },
);

export default ChatInput;
